import { BigNumber } from 'bignumber.js';
import { useEffect, useMemo, useState } from 'react';
import { useAccount as useWagmiAccount } from 'wagmi';
import { IS_IN_D9_APP } from '@/lib/constants';
import type { CrossChainRoute } from '@/lib/query-options';
import { parseD9Address } from '@/lib/utils/d9';
import { useD9Address } from './use-d9';
import { useTronAddress } from './use-tron';

export function useTransferState(routes: [CrossChainRoute, ...CrossChainRoute[]]) {
  const { address: evmAddress } = useWagmiAccount();
  const tronAddress = useTronAddress();
  const d9Address = useD9Address();

  const chainAddressMap = useMemo(
    () => ({
      BSC: evmAddress,
      TRON: tronAddress,
      D9: d9Address,
    }),
    // oxlint-disable-next-line exhaustive-deps 需要这样
    [],
  );

  const initialRoute = useMemo(() => {
    if (IS_IN_D9_APP) {
      return routes.find((route) => route.fromChain === 'D9') ?? routes[0];
    }
    for (const chain of Object.keys(chainAddressMap) as Array<
      keyof typeof chainAddressMap
    >) {
      if (chainAddressMap[chain]) {
        const found = routes.find((route) => route.fromChain === chain);
        if (found) return found;
      }
    }
    return routes[0];
  }, [routes, chainAddressMap]);

  const [route, setRoute] = useState(initialRoute);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('0.00');
  const [receiver, setReceiver] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);

  const fromAddress = useMemo(() => {
    switch (route.fromChain) {
      case 'BSC':
        return evmAddress;
      case 'TRON':
        return tronAddress;
      case 'D9':
        return d9Address;
    }
  }, [route.fromChain, evmAddress, tronAddress, d9Address]);

  const fromAmountBN = useMemo(() => {
    const bn = BigNumber(fromAmount);
    return bn.isNaN() ? BigNumber(0) : bn;
  }, [fromAmount]);

  const validUserId = useMemo(() => {
    // 应该总是D9地址
    try {
      if (route.fromChain === 'D9') {
        return parseD9Address(fromAddress ?? '');
      } else {
        return parseD9Address(receiver);
      }
    } catch {
      return undefined;
    }
  }, [receiver, fromAddress, route]);

  useEffect(() => {
    const value = fromAmountBN.plus(
      fromAmountBN.multipliedBy(route.adjustmentBasisPoints / 100000),
    );
    const truncated = value
      .multipliedBy(100)
      .integerValue(BigNumber.ROUND_DOWN)
      .dividedBy(100);
    setToAmount(truncated.toFixed(2));
  }, [fromAmountBN, route.adjustmentBasisPoints]);

  return {
    route,
    setRoute,
    fromAmount,
    setFromAmount,
    toAmount,
    receiver,
    setReceiver,
    confirmOpen,
    setConfirmOpen,
    progressOpen,
    setProgressOpen,
    jobId,
    setJobId,
    fromAddress,
    fromAmountBN,
    validUserId,
  };
}


